import AddIcon from '@library/assets/images/icons/add-icon.svg';
import { Button } from '@library/components/buttons';
import { Input } from '@library/components/inputs';
import type { MissionEventInput } from '@library/types/missions';
import { Select } from '@library/components/selects';
import type { SelectOption } from '@library/types/utils';
import TrashIcon from '@library/assets/images/icons/trash-icon.svg';
import { cn } from '@library/component-utils/cn';
import { formatLabelByKey } from '@bo-utils/common';
import { useGetMissionEventDefinitionsQuery } from '@bo-services/promotionsApi';
import { useMemo } from 'react';

type MissionEventsFormInputsProps = {
  inputs: MissionEventInput[];
  setInputs: (inputs: MissionEventInput[]) => void;
};

export const MissionEventsFormInputs = ({
  inputs,
  setInputs
}: MissionEventsFormInputsProps) => {
  const { data, isLoading } = useGetMissionEventDefinitionsQuery(undefined);
  const maxNumOfFields = useMemo(
    () => data?.event_definitions.length,
    [data?.event_definitions]
  );
  const options = useMemo<SelectOption[]>(() => {
    if (data) {
      return data.event_definitions.map((def) => ({
        key: def.id,
        name: formatLabelByKey(def.name),
        value: def.id
      }));
    }

    return [];
  }, [data]);

  const handleAddField = () => {
    if (inputs.length === maxNumOfFields) {
      return;
    }

    setInputs([...inputs, { event_definition_id: '', occurrences: 1 }]);
  };

  const handleInputChange = (index: number, value: number) => {
    const newInputs = [...inputs];
    newInputs[index].occurrences = value;
    setInputs(newInputs);
  };

  const handleSelectChange = (index: number, value: string) => {
    const newInputs = [...inputs];
    newInputs[index].event_definition_id = value;
    setInputs(newInputs);
  };

  const handleRemoveField = (index: number) => {
    const newInputs = inputs.filter((_, i) => i !== index);
    setInputs(newInputs);
  };

  if (isLoading) {
    return 'Future home of skeleton form';
  }

  return (
    <>
      {inputs.map(({ event_definition_id, occurrences }, index) => (
        <div
          key={`event${index + 1}`}
          className="flex items-end gap-x-4 self-start"
        >
          <div className="flex flex-col gap-y-2">
            <label
              htmlFor="0"
              className="text-soClean-100 rounded-md text-xs uppercase leading-6"
            >
              Event{' '}
              <span className="text-soClean-180 text-xs leading-6">
                #{index + 1}
              </span>
            </label>
            <Select
              className="min-w-[200px]"
              label="Event"
              onChange={(option) => handleSelectChange(index, option.value)}
              options={options}
              size="small"
              testId="event-def"
              value={event_definition_id}
            />
          </div>

          <div className="flex flex-col gap-y-2">
            <Input
              className={cn(
                'bg-soFresh-110 text-soClean-100 border-soFresh-140 placeholder:text-soClean-170 block w-full rounded-md border py-1.5 sm:text-sm sm:leading-6',
                'disabled:cursor-not-allowed',
                'h-10 w-20'
              )}
              labelName="Occurrences"
              min={1}
              type="number"
              value={occurrences}
              onValueChange={(value) => handleInputChange(index, Number(value))}
            />
          </div>

          <div className="flex flex-col gap-y-2">
            <span className="text-soClean-100 rounded-md text-xs uppercase leading-6">
              &nbsp;
            </span>
            <Button
              className={cn(
                'bg-soFresh-110 border-soFresh-140 disabled:bg-soFresh-130 flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-solid text-[13px] font-medium',
                'hover:bg-soFresh-120'
              )}
              data-testid="TrashIconButton"
              onClick={() => handleRemoveField(index)}
              type="button"
              disabled={inputs.length === 1}
            >
              <img
                className={cn('h-4 w-4 shrink-0')}
                src={TrashIcon}
                alt="remove event"
              />
            </Button>
          </div>
        </div>
      ))}
      <div className="border-soFresh-190/50 border-t border-dashed py-4">
        <Button
          className="border-soFresh-140 hover:bg-soFresh-120 bg-soFresh-110 disabled:bg-soFresh-130 text-soClean-140 flex items-center gap-2 rounded-md border p-2 pr-4 text-sm"
          onClick={handleAddField}
          disabled={inputs.length === maxNumOfFields}
        >
          <img className={cn('h-4 w-4 shrink-0')} src={AddIcon} alt="add" />
          Add Event
        </Button>
      </div>
    </>
  );
};
